import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ErrorState, Skeleton } from '@/components/ui/Feedback';
import { useAuth } from '@/features/auth/AuthContext';
import { cn } from '@/lib/cn';
import { BandsTab } from './BandsTab';
import { StructureTab } from './StructureTab';
import { VersionBar } from './VersionBar';
import { SCOPE_LABEL, instrumentApi } from './api';

const TABS = ['Struktur', 'Kategori'] as const;
type Tab = (typeof TABS)[number];

/** L-13b — Isi instrumen. Ketiga tab bisa disunting Super Admin. */
export function InstrumentDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('Struktur');
  /** null = versi aktif (draf kalau ada, kalau tidak yang terbit). */
  const [viewing, setViewing] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['instrument', id, viewing],
    queryFn: () => instrumentApi.get(id, viewing ?? undefined),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  const allIndicators = data.sections.flatMap((s) => s.dimensions.flatMap((d) => d.indicators));

  // Instrumen Capaian Program tidak mendeklarasikan label skala, padahal
  // kriterianya tetap 1–4. Batas atas diambil dari kriteria yang benar-benar
  // ada, bukan dari jumlah label — kalau tidak, headernya menulis "Skala 1–0".
  const maxScore = Math.max(
    data.version.scaleLabels.length,
    ...allIndicators.flatMap((i) => i.criteria.map((c) => c.score)),
    0,
  );

  return (
    <div className="flex flex-col gap-5">
      <Link to="/pengaturan/instrumen" className="text-sm text-ink-secondary hover:text-primary">
        ← Instrumen & Bobot
      </Link>

      <Card className="min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="rounded-full bg-primary-soft px-2.5 py-1 text-[13px] font-medium text-primary">
                {SCOPE_LABEL[data.scope]}
              </span>
              <span
                className={cn(
                  'rounded-full px-2.5 py-1 text-[13px] font-medium',
                  data.version.status === 'published'
                    ? 'bg-success-soft text-success'
                    : data.version.status === 'draft'
                      ? 'bg-warn-soft text-[#9a6400]'
                      : 'bg-app text-ink-secondary',
                )}
              >
                {data.version.status === 'published'
                  ? 'Terbit'
                  : data.version.status === 'draft'
                    ? 'Draf'
                    : 'Arsip'}{' '}
                · v{data.version.version}
              </span>
            </div>
            <h1 className="mt-2.5 text-2xl font-semibold text-ink">{data.name}</h1>
            <p className="mt-1 max-w-3xl text-ink-secondary">{data.description}</p>
          </div>

          <div className="flex flex-col items-end gap-3">
            <dl className="flex gap-6 text-sm">
              <div>
                <dt className="text-ink-secondary">Indikator</dt>
                <dd className="tabular text-2xl font-semibold text-ink">{allIndicators.length}</dd>
              </div>
              <div>
                <dt className="text-ink-secondary">Skala</dt>
                <dd className="tabular text-2xl font-semibold text-ink">1–{maxScore}</dd>
              </div>
            </dl>
            {user?.role === 'super_admin' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/pengaturan/instrumen/${id}/impor`)}
              >
                ⭳ Impor Struktur
              </Button>
            )}
          </div>
        </div>
      </Card>

      <VersionBar data={data} viewing={viewing} onView={setViewing} />

      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'min-h-11 shrink-0 border-b-2 px-4 text-[15px] font-medium',
              tab === t
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-secondary hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Struktur' && <StructureTab data={data} />}
      {tab === 'Kategori' && <BandsTab data={data} />}
    </div>
  );
}
